import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useBxpWebStargateClient } from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import {
	FASTSHIFT_DC_CONNECTION_STATUSES,
	type FastshiftDcConnectionStatus,
	type FastshiftDcProductType,
	type GetFastshiftOrganizationDcInstancesResponseData,
} from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import { type MessageDescriptor, useIntl } from 'react-intl';

import Heading from '@atlaskit/heading';
import Lozenge from '@atlaskit/lozenge';
import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';

import { ActionSubjectIdKey } from '@/analytics/constants';
import { GenericErrorDisplay } from '@/common/displays/src/generic-error-display/generic-error-display';
import {
	FASTSHIFT_PRODUCT_VARIANTS,
	FastshiftProductIcon,
} from '@/common/fastshift-product-icon/src/fastshift-product-icon';
import {
	DEFAULT_CONTROLS_PAYLOAD,
	FastshiftTable,
	type ControlsPayload,
	type TableHeads,
	type TableRows,
} from '@/common/fastshift-table';
import { useFastshiftDispatch, useFastshiftSelector, type FastshiftDispatchPromise } from '@/state';
import { getFastshiftOrganizationDcInstances } from '@/state/actions';

import { useOrganizationDetailsSectionAnalytics } from '../hooks/use-organization-details-section-analytics';

import { messages } from './messages';

type LozengeAppearance = NonNullable<React.ComponentProps<typeof Lozenge>['appearance']>;

const CONNECTION_STATUS_CONFIG: Record<
	FastshiftDcConnectionStatus,
	{ appearance: LozengeAppearance; message: MessageDescriptor }
> = {
	[FASTSHIFT_DC_CONNECTION_STATUSES.CONNECTED]: {
		appearance: 'success',
		message: messages.connectionConnected,
	},
	[FASTSHIFT_DC_CONNECTION_STATUSES.INITIALIZED]: {
		appearance: 'information',
		message: messages.connectionInitialized,
	},
	[FASTSHIFT_DC_CONNECTION_STATUSES.REGENERATING]: {
		appearance: 'information',
		message: messages.connectionRegenerating,
	},
	[FASTSHIFT_DC_CONNECTION_STATUSES.DISABLED]: {
		appearance: 'neutral',
		message: messages.connectionDisconnected,
	},
	[FASTSHIFT_DC_CONNECTION_STATUSES.EXPIRED]: {
		appearance: 'warning',
		message: messages.connectionExpired,
	},
	[FASTSHIFT_DC_CONNECTION_STATUSES.INTERRUPTED]: {
		appearance: 'danger',
		message: messages.connectionInterrupted,
	},
	[FASTSHIFT_DC_CONNECTION_STATUSES.FAILED]: {
		appearance: 'danger',
		message: messages.connectionFailed,
	},
	[FASTSHIFT_DC_CONNECTION_STATUSES.OFFLINE]: {
		appearance: 'neutral',
		message: messages.connectionOffline,
	},
};

const UNKNOWN_CONNECTION_STATUS_CONFIG: {
	appearance: LozengeAppearance;
	message: MessageDescriptor;
} = {
	appearance: 'neutral',
	message: messages.connectionUnknown,
};

export const getConnectionStatusConfig = (status: string) =>
	CONNECTION_STATUS_CONFIG[status as FastshiftDcConnectionStatus] ??
	UNKNOWN_CONNECTION_STATUS_CONFIG;

const PRODUCT_MESSAGE_BY_TYPE: Record<FastshiftDcProductType, MessageDescriptor> = {
	JIRA: messages.jiraProduct,
	CONFLUENCE: messages.confluenceProduct,
};

const getProductMessage = (productType: FastshiftDcProductType | null): MessageDescriptor =>
	(productType && PRODUCT_MESSAGE_BY_TYPE[productType]) ?? messages.unknownProduct;

const EMPTY_INSTANCES: GetFastshiftOrganizationDcInstancesResponseData = [];

interface OrganizationDcInstancesTableProps {
	domainId: string;
	orgId: string;
}

export const OrganizationDcInstancesTable = ({
	domainId,
	orgId,
}: OrganizationDcInstancesTableProps) => {
	const { formatMessage } = useIntl();
	const stargateClient = useBxpWebStargateClient();
	const dispatch = useFastshiftDispatch();
	const activeRequestRef = useRef<FastshiftDispatchPromise<unknown> | null>(null);
	const [controls, setControls] = useState<ControlsPayload>(DEFAULT_CONTROLS_PAYLOAD);
	const {
		domainId: stateDomainId,
		orgId: stateOrgId,
		dcInstances,
	} = useFastshiftSelector((state) => state.organizationDetail);

	const loadInstances = useCallback(() => {
		activeRequestRef.current?.abort();
		activeRequestRef.current = dispatch(
			getFastshiftOrganizationDcInstances({
				stargateClient,
				domainId,
				orgId,
			}),
		);
	}, [dispatch, domainId, orgId, stargateClient]);

	useEffect(() => {
		setControls(DEFAULT_CONTROLS_PAYLOAD);
		loadInstances();

		return () => {
			activeRequestRef.current?.abort();
			activeRequestRef.current = null;
		};
	}, [loadInstances]);

	const hasMatchingOrg = stateDomainId === domainId && stateOrgId === orgId;
	const instances = (hasMatchingOrg ? dcInstances.data : null) ?? EMPTY_INSTANCES;
	const totalElements = instances.length;
	const isTableLoading =
		!hasMatchingOrg || dcInstances.pending || (dcInstances.data === null && !dcInstances.error);
	useOrganizationDetailsSectionAnalytics({
		organizationId: orgId,
		actionSubjectId: ActionSubjectIdKey.ORGANIZATION_DC_INSTANCES_TABLE,
		status: isTableLoading
			? null
			: dcInstances.error
				? 'error'
				: totalElements
					? 'normal'
					: 'empty',
	});
	const totalPages = Math.ceil(totalElements / controls.size);
	const visibleInstances = useMemo(() => {
		const firstVisibleIndex = controls.page * controls.size;

		return instances.slice(firstVisibleIndex, firstVisibleIndex + controls.size);
	}, [controls.page, controls.size, instances]);

	const head = useMemo<TableHeads>(
		() => ({
			cells: [
				{
					key: 'source',
					content: (
						<Heading size="xxsmall" as="span">
							{formatMessage(messages.sourceColumnHeading)}
						</Heading>
					),
					width: 70,
				},
				{
					key: 'connection',
					content: (
						<Heading size="xxsmall" as="span">
							{formatMessage(messages.connectionColumnHeading)}
						</Heading>
					),
					width: 30,
				},
			],
		}),
		[formatMessage],
	);

	const rows = useMemo<TableRows>(
		() =>
			visibleInstances.map((instance) => {
				const connectionStatus = getConnectionStatusConfig(instance.status);
				const productMessage = getProductMessage(instance.productType);

				return {
					key: instance.linkId,
					cells: [
						{
							key: 'source',
							content: (
								<Inline space="space.100" alignBlock="center">
									<FastshiftProductIcon
										productName={instance.productType}
										variant={FASTSHIFT_PRODUCT_VARIANTS.SOURCE}
									/>
									<Stack space="space.025">
										<Text>{formatMessage(productMessage)}</Text>
										<Text color="color.text.subtle">{instance.baseUrl}</Text>
									</Stack>
								</Inline>
							),
						},
						{
							key: 'connection',
							content: (
								<Lozenge
									appearance={connectionStatus.appearance}
									testId={`organization-dc-instance-connection-${instance.linkId}`}
								>
									{formatMessage(connectionStatus.message)}
								</Lozenge>
							),
						},
					],
				};
			}),
		[formatMessage, visibleInstances],
	);

	return (
		<Stack space="space.100">
			<Heading size="small" as="h2">
				{formatMessage(messages.sectionHeading)}
			</Heading>
			<FastshiftTable
				id="organization-dc-instances"
				testId="organization-dc-instances"
				tableCaption={formatMessage(messages.tableCaption)}
				isTableLoading={isTableLoading}
				isTableError={hasMatchingOrg && Boolean(dcInstances.error)}
				errorComponent={
					<GenericErrorDisplay
						customMessage={formatMessage(messages.errorMessage)}
						onRefresh={loadInstances}
					/>
				}
				head={head}
				rows={rows}
				controls={{
					pagination: {
						page: controls.page,
						size: controls.size,
						totalElements,
						totalPages,
						hasNext: controls.page + 1 < totalPages,
						hasPrevious: controls.page > 0,
						sorted: false,
					},
					controls,
					onSetControlsPayload: setControls,
				}}
			/>
		</Stack>
	);
};
